import React, { useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Wand2, Sparkles, Copy, Calculator, ReceiptText, Camera } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import ReviewBadges from "@/components/ReviewBadges";
import { Button } from "@/components/ui/button";

// slug -> content. packKey must match backend STARTER_RATE_PACKS keys.
const TRADES = {
  electrical: {
    packKey: "electrical",
    label: "Electrical",
    metaTitle: "Electrical Estimating & Takeoff Software (UK) — AI Symbol Counting | TradeOff",
    metaDesc: "AI takeoff and estimating software for UK electrical contractors. Auto-count sockets, lights and symbols, read schedules, price from an electrical rate library, and bill with CIS & VAT reverse charge. Start free.",
    h1: "Electrical estimating & takeoff software",
    sub: "Auto-count sockets, downlights and symbols, read schedules with AI, and price from a ready-made electrical rate library — then bill with CIS and VAT reverse charge built in.",
    highlights: [
      { icon: Copy, t: "Auto-count symbols", d: "Box one socket, light or data point and TradeOff finds every match on the sheet — no more manual tallying." },
      { icon: Sparkles, t: "Read electrical schedules", d: "AI pulls quantities from legends and schedules straight into your estimate." },
      { icon: Calculator, t: "Electrical rate library", d: "Start with sockets, downlights, consumer units, rewires and test & inspection rates — edit to your prices." },
      { icon: ReceiptText, t: "CIS & reverse charge", d: "Applications for Payment with CIS deductions and VAT domestic reverse charge handled automatically." },
    ],
  },
  plumbing: {
    packKey: "plumbing_heating",
    label: "Plumbing & heating",
    metaTitle: "Plumbing & Heating Estimating Software (UK) — AI Takeoff | TradeOff",
    metaDesc: "AI takeoff and estimating for UK plumbing & heating engineers. Count fittings, read schedules, price from a plumbing rate library, and bill with CIS & VAT reverse charge. Start free.",
    h1: "Plumbing & heating estimating software",
    sub: "Count radiators, sanitaryware and fittings, read schedules with AI, and price from a ready-made plumbing & heating rate library — with UK billing built in.",
    highlights: [
      { icon: Copy, t: "Count fittings fast", d: "Auto-count radiators, sanitaryware and outlets across the drawing in seconds." },
      { icon: Sparkles, t: "Read schedules with AI", d: "Pull sanitaryware and fixture quantities from schedules straight into your estimate." },
      { icon: Calculator, t: "Plumbing rate library", d: "Boilers, radiators, first-fix, WCs, basins and pipework rates ready to edit to your prices." },
      { icon: ReceiptText, t: "CIS & reverse charge", d: "Applications for Payment with CIS and VAT reverse charge handled automatically." },
    ],
  },
  groundworks: {
    packKey: "groundworks",
    label: "Groundworks & drainage",
    metaTitle: "Groundworks Estimating & Takeoff Software (UK) | TradeOff",
    metaDesc: "AI takeoff and estimating for UK groundworks & drainage contractors. Measure areas and runs, price from a groundworks rate library, and bill with CIS & VAT reverse charge. Start free.",
    h1: "Groundworks & drainage estimating software",
    sub: "Measure excavations, slabs, drainage runs and paving, and price from a ready-made groundworks rate library — then run CIS-ready progress billing.",
    highlights: [
      { icon: Wand2, t: "Fast area & run takeoff", d: "Trace excavations, slabs and drainage runs in the browser with AI scale detection." },
      { icon: Sparkles, t: "AI first pass", d: "Detect the scale and pull schedule quantities in one click before you refine." },
      { icon: Calculator, t: "Groundworks rate library", d: "Excavation, concrete, drainage, chambers and block paving rates ready to edit." },
      { icon: ReceiptText, t: "CIS & reverse charge", d: "Applications for Payment with CIS and VAT reverse charge handled automatically." },
    ],
  },
  carpentry: {
    packKey: "carpentry",
    label: "Carpentry & joinery",
    metaTitle: "Carpentry & Joinery Estimating Software (UK) — AI Takeoff | TradeOff",
    metaDesc: "AI takeoff and estimating for UK carpenters & joiners. Count doors and openings, read schedules, price from a joinery rate library, and bill with CIS & VAT reverse charge. Start free.",
    h1: "Carpentry & joinery estimating software",
    sub: "Count doors, openings and runs of skirting and architrave, read schedules with AI, and price from a ready-made joinery rate library — with UK billing built in.",
    highlights: [
      { icon: Copy, t: "Count doors & openings", d: "Auto-count doors, windows and repeated joinery items across the sheet." },
      { icon: Sparkles, t: "Read door/window schedules", d: "AI pulls schedule quantities straight into your estimate." },
      { icon: Calculator, t: "Joinery rate library", d: "Doors, skirting, architrave, studwork, floor joists and fitted units ready to edit." },
      { icon: ReceiptText, t: "CIS & reverse charge", d: "Applications for Payment with CIS and VAT reverse charge handled automatically." },
    ],
  },
  plastering: {
    packKey: "plastering_drylining",
    label: "Plastering & drylining",
    metaTitle: "Plastering & Drylining Estimating Software (UK) — AI Takeoff | TradeOff",
    metaDesc: "AI takeoff and estimating for UK plasterers & dryliners. Measure wall and ceiling areas, price from a plastering rate library, and bill with CIS & VAT reverse charge. Start free.",
    h1: "Plastering & drylining estimating software",
    sub: "Measure wall and ceiling areas fast, price from a ready-made plastering & drylining rate library, and run CIS-ready progress billing — all in the browser.",
    highlights: [
      { icon: Wand2, t: "Fast area takeoff", d: "Trace walls and ceilings with AI scale detection — m² totals in seconds." },
      { icon: Sparkles, t: "AI first pass", d: "Detect the scale and pull schedule quantities in one click before you refine." },
      { icon: Calculator, t: "Plastering rate library", d: "Dot & dab, skim, stud partitions, tape & joint and render rates ready to edit." },
      { icon: ReceiptText, t: "CIS & reverse charge", d: "Applications for Payment with CIS and VAT reverse charge handled automatically." },
    ],
  },
  painting: {
    packKey: "painting_decorating",
    label: "Painting & decorating",
    metaTitle: "Painting & Decorating Estimating Software (UK) — AI Takeoff | TradeOff",
    metaDesc: "AI takeoff and estimating for UK painters & decorators. Measure areas, price from a decorating rate library, and bill with CIS & VAT reverse charge. Start free.",
    h1: "Painting & decorating estimating software",
    sub: "Measure walls, ceilings and woodwork, price from a ready-made decorating rate library, and bill with CIS and VAT reverse charge built in.",
    highlights: [
      { icon: Wand2, t: "Fast area takeoff", d: "Trace surfaces with AI scale detection — accurate m² and linear metres in seconds." },
      { icon: Sparkles, t: "AI first pass", d: "Detect the scale and pull schedule quantities in one click." },
      { icon: Calculator, t: "Decorating rate library", d: "Emulsion, gloss, prep, lining and exterior masonry rates ready to edit." },
      { icon: ReceiptText, t: "CIS & reverse charge", d: "Applications for Payment with CIS and VAT reverse charge handled automatically." },
    ],
  },
  flooring: {
    packKey: "flooring_tiling",
    label: "Flooring & tiling",
    metaTitle: "Flooring & Tiling Estimating Software (UK) — AI Takeoff | TradeOff",
    metaDesc: "AI takeoff and estimating for UK flooring & tiling contractors. Measure floor and wall areas, price from a flooring rate library, and bill with CIS & VAT reverse charge. Start free.",
    h1: "Flooring & tiling estimating software",
    sub: "Measure floor and wall areas fast, price from a ready-made flooring & tiling rate library, and run CIS-ready progress billing — all in the browser.",
    highlights: [
      { icon: Wand2, t: "Fast area takeoff", d: "Trace rooms and wall areas with AI scale detection — m² totals in seconds." },
      { icon: Sparkles, t: "AI first pass", d: "Detect the scale and pull schedule quantities in one click." },
      { icon: Calculator, t: "Flooring rate library", d: "Laminate, LVT, ceramic, carpet and self-levelling screed rates ready to edit." },
      { icon: ReceiptText, t: "CIS & reverse charge", d: "Applications for Payment with CIS and VAT reverse charge handled automatically." },
    ],
  },
  brickwork: {
    packKey: "brick_block",
    label: "Brickwork & blockwork",
    metaTitle: "Bricklaying & Blockwork Estimating Software (UK) — AI Takeoff | TradeOff",
    metaDesc: "AI takeoff and estimating for UK bricklayers. Measure wall areas, price from a brick & block rate library, and bill with CIS & VAT reverse charge. Start free.",
    h1: "Bricklaying & blockwork estimating software",
    sub: "Measure wall areas and piers, price from a ready-made brick & block rate library, and run CIS-ready progress billing — all in the browser.",
    highlights: [
      { icon: Wand2, t: "Fast area takeoff", d: "Trace walls and piers with AI scale detection — m² and linear metres in seconds." },
      { icon: Sparkles, t: "AI first pass", d: "Detect the scale and pull schedule quantities in one click." },
      { icon: Calculator, t: "Brick & block rate library", d: "Facing brick, blockwork, cavity walls, piers and repointing rates ready to edit." },
      { icon: ReceiptText, t: "CIS & reverse charge", d: "Applications for Payment with CIS and VAT reverse charge handled automatically." },
    ],
  },
};

const SAMPLE_IMG = "https://images.pexels.com/photos/34573691/pexels-photo-34573691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TradeLanding = ({ slug }) => {
  const navigate = useNavigate();
  const data = TRADES[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!data) return <Navigate to="/" replace />;

  const go = () => navigate(`/register?trade=${data.packKey}`);

  return (
    <div className="bg-white">
      <Seo title={data.metaTitle} description={data.metaDesc} path={`/${slug}-estimating-software`} />
      <MarketingNav />

      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">{data.label} · AI takeoff</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.08]">{data.h1}</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">{data.sub}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={go} data-testid="trade-hero-cta" className="bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base">
              Start free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/pricing")} className="h-12 px-7 text-base border-slate-300">See pricing</Button>
          </div>
          <p className="mt-4 text-sm text-slate-500">No credit card required · Your {data.label.toLowerCase()} rate library is loaded for you</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-5">
            {data.highlights.map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-xl border border-slate-200 bg-slate-50/50 p-6"
                data-testid={`trade-highlight-${i}`}
              >
                <div className="h-11 w-11 rounded-lg bg-blue-600/10 flex items-center justify-center"><c.icon className="h-5 w-5 text-blue-600" /></div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{c.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button size="lg" onClick={go} className="bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base">Try it free <ArrowRight className="ml-2 h-4 w-4" /></Button>
            <span className="inline-flex items-center gap-2 text-sm text-slate-500"><Camera className="h-4 w-4 text-blue-600" /> Or snap a drawing on your phone and get quantities back</span>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-y border-slate-200" data-testid="trade-proof">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <img src={SAMPLE_IMG} alt={`${data.label} takeoff on a construction drawing`} className="w-full h-72 object-cover" loading="lazy" />
            <span className="absolute bottom-3 left-3 bg-white/90 backdrop-blur text-xs font-medium text-slate-700 px-2.5 py-1 rounded-md">From drawing → priced estimate</span>
          </div>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Point AI at your {data.label.toLowerCase()} drawing</h2>
            <p className="mt-4 text-slate-600">Upload a PDF or scanned drawing (or snap a photo on site). TradeOff detects the scale, reads the schedules and counts the symbols — then prices it from your rate library. Review every number and you're ready to bill.</p>
            <div className="mt-5 space-y-2">
              {["Scale detected automatically", "Quantities read from schedules & legends", "Priced from your own rates, adjusted to your region"].map((f) => (
                <p key={f} className="flex items-center gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-blue-600" />{f}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl font-bold text-slate-900">Built for UK subcontractors</h2>
          <p className="mt-3 text-slate-600">Takeoff, estimate, bill and get paid in one browser-based tool — with CIS, VAT reverse charge and Xero handled for you.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-700">
            {["AI takeoff", "One-click AI pricing", "CIS & VAT reverse charge", "Applications for Payment", "Xero sync", "Card & BACS payments"].map((f) => (
              <span key={f} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-blue-600" />{f}</span>
            ))}
          </div>
          <div className="mt-8"><ReviewBadges /></div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: "#16305C" }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Price your next {data.label.toLowerCase()} job in minutes</h2>
          <p className="mt-4 text-slate-300">Start free — your {data.label.toLowerCase()} rate library is ready the moment you sign up.</p>
          <Button size="lg" onClick={go} data-testid="trade-cta-btn" className="mt-8 bg-blue-600 hover:bg-blue-500 h-12 px-8 text-base">
            Create your workspace <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TradeLanding;
